import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import {
    Table, TableBody, TableContainer, TableRow, TableHead, Paper, TableCell,
    CircularProgress, Alert, Typography,
    Box,
    TextField,
    Checkbox,
    Button,
    Divider,
    Select,
    MenuItem
} from '@mui/material';
import {
    getQuenchTeamVideosData,
    postQuenchTeamVideosData
} from '../../../../../../actions/conservation-burns/conservation-burn-actions';
import { v4 as uuidv4 } from 'uuid';
import useTsPaginator from 'ts-paginator';
import Paginator from '../../../../../layout/Pagination';

export default function CbPileVariabilityData({ projectKey }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [quenchTeamVideosState, setQuenchTeamVideosState] = useState([]);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const quenchTeamVideosData = useSelector(
        (state) => state.conservationBurnsReducer.quenchTeamVideosData
    );
    const tableRecordCount = useSelector(
        (state) => state.conservationBurnsReducer.quenchTeamVideosData?.pagination?.total_records
    );

    const {
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    } = useTsPaginator(+tableRecordCount || 0, 0);

    // Fetch on mount / project change
    useEffect(() => {
        dispatch(getQuenchTeamVideosData(projectKey, rowsPerPage, currentPage));
    }, [dispatch, projectKey, rowsPerPage, currentPage]);

    // Turn off loading when data arrives
    useEffect(() => {
        if (quenchTeamVideosData) {
            setLoading(false);
        }
    }, [quenchTeamVideosData]);

    useEffect(() => {
        _handleChangePage(0);
        _handleChangeRowsPerPage(10);
        _handleChangeTotalRecordCount(+tableRecordCount);
    }, [projectKey, tableRecordCount]);

    // Build local editable state from reducer data
    useEffect(() => {
        const stateArray = quenchTeamVideosData?.data?.map((item) => ({
            id: item.id,
            rowId: uuidv4(),
            batchId: item.batchId,
            batch: item.batch,
            sampleNumber: item.sampleNumber,
            videoReferenceId: item.videoReferenceId ?? '',
            hotCoalBucketMass: item.hotCoalBucketMass ?? '',
            sampleTaken: !!item.sampleTaken,
            videoOptions: item.videoOptions ?? [],
            dirty: false,
        })) || [];

        setQuenchTeamVideosState(stateArray);
    }, [quenchTeamVideosData]);

    if (loading) return <CircularProgress />;

    if (!quenchTeamVideosData?.data?.length) {
        return (
            <Alert severity="info" sx={{ width: 'fit-content' }}>
                No Conservation Burn data for this project
            </Alert>
        );
    }

    const handleInputChange = (e, dataId, field) => {
        const value = field === 'sampleTaken' ? e.target.checked : e.target.value;
        setQuenchTeamVideosState((prevState) =>
            prevState.map((item) => {
                if (item.rowId === dataId) {
                    return {
                        ...item,
                        [field]: value,
                        dirty: true,
                    };
                }
                return item;
            })
        );
    };

    const handleSubmit = async () => {
        const dirtyRows = quenchTeamVideosState.filter((item) => item.dirty);
        if (!dirtyRows.length) {
            setError('No changes to save');
            return;
        }
        setError(null);
        setSaving(true);
        try {
            await dispatch(postQuenchTeamVideosData(projectKey, {
                rows: dirtyRows.map((row) => ({
                    batchId: row.batchId,
                    sampleNumber: row.sampleNumber,
                    videoReferenceId: row.videoReferenceId || null,
                    hotCoalBucketMass: row.hotCoalBucketMass === '' ? null : row.hotCoalBucketMass,
                    sampleTaken: !!row.sampleTaken,
                })),
            }));
            await dispatch(getQuenchTeamVideosData(projectKey, rowsPerPage, currentPage));
        } catch (e) {
            setError('Unable to save quench team sample data');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
            <Divider />
            <Typography variant='h5'>Quench Team Samples</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Batch ID</TableCell>
                                <TableCell>Sample #</TableCell>
                                <TableCell>Video Ref</TableCell>
                                <TableCell>Raked &amp; Quenched Bucket Mass</TableCell>
                                <TableCell>Sample Taken</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {quenchTeamVideosState.map((qtv, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{qtv.batch}</TableCell>
                                    <TableCell>{qtv.sampleNumber}</TableCell>
                                    <TableCell sx={{ minWidth: 220 }}>
                                        <Select
                                            fullWidth
                                            displayEmpty
                                            value={qtv.videoReferenceId}
                                            onChange={(e) => handleInputChange(e, qtv.rowId, 'videoReferenceId')}
                                        >
                                            <MenuItem value="">
                                                Select video from dropdown
                                            </MenuItem>
                                            {qtv.videoOptions.map((opt) => (
                                                <MenuItem key={opt.id} value={opt.id}>
                                                    {opt.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            name='hotCoalBucketMass'
                                            value={qtv.hotCoalBucketMass}
                                            onChange={(e) => handleInputChange(e, qtv.rowId, 'hotCoalBucketMass')}
                                            type="number"
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Checkbox
                                            checked={!!qtv.sampleTaken}
                                            onChange={(e) => handleInputChange(e, qtv.rowId, 'sampleTaken')}
                                            inputProps={{ 'aria-label': 'Sample taken' }}
                                        />
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Button
                    variant='contained'
                    sx={{ width: 'fit-content', mt: 2 }}
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                {error ? <Alert severity='error' sx={{ my: 2 }}>{error}</Alert> : null}
                <Paginator
                    totalRecordCount={totalRecordCount}
                    rowsPerPage={rowsPerPage}
                    currentPage={currentPage}
                    handleChangePage={_handleChangePage}
                    handleChangeRowsPerPage={_handleChangeRowsPerPage}
                    determinePaginationMessage={_determinePaginationMessage}
                    determinePaginationDisabledState={_determinePaginationDisabledState}
                    determineRowsPerPageOptions={_determineRowsPerPageOptions}
                />
            </Box>
        </Box>
    );
}